import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { type DocumentSubtype, type DocumentType } from '../../models';
import { LocaleService } from '../../services/locale/locale.service';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { getCategoryKindFromCode, getDocKindFromSubtypeCode } from '../../utils/document.utils';
import { LocalizedDescriptionPipe, LocalizedNamePipe } from '../../shared/pipes';
import { localizedName } from '../../utils/locale.utils';
import { CategoryIconComponent } from '../category-icon/category-icon';
import { KindIconComponent } from '../kind-icon/kind-icon';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { StepIndicatorComponent, StepConfig } from '../step-indicator/step-indicator';

export interface ContentTypeSelection {
  typeId: string;
  subtypeId: string;
  subtypeCode: string;
  /** The subtype's English name and its Danish one (as the backend stores them), so a caller that
   *  patches a document record keeps `subtype_name` and `subtype_name_da` in step. */
  subtypeName: string;
  subtypeNameDa: string;
  /** `null` (not `undefined`) for non-custom subtypes — callers that stage
   *  this onto an update payload need an explicit clear signal, since
   *  `undefined` reads as "field omitted" rather than "clear the label". */
  customTypeName: string | null;
}

type SubStep = 'category' | 'subtype';

@Component({
  selector: 'app-content-type-modal',
  imports: [
    ModalComponent,
    TranslocoModule,
    MatIconModule,
    CategoryIconComponent,
    KindIconComponent,
    StepIndicatorComponent,
    LocalizedNamePipe,
    LocalizedDescriptionPipe,
  ],
  templateUrl: './content-type-modal.html',
  styleUrl: './content-type-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentTypeModalComponent implements OnInit {
  protected readonly getCategoryKindFromCode = getCategoryKindFromCode;
  protected readonly getDocKindFromSubtypeCode = getDocKindFromSubtypeCode;

  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly orgManagementApi = inject(OrgManagementApi);
  protected readonly locale = inject(LocaleService).locale;
  private readonly translationVersion = signal(0);

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));
  }

  ngOnInit(): void {
    this.orgManagementApi
      .getDocumentTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this._documentTypes.set(res.document_types),
        error: () => this._documentTypes.set([]),
      });
  }

  readonly isVisible = input(false);
  readonly selected = output<ContentTypeSelection>();
  readonly cancelled = output<void>();

  readonly _documentTypes = signal<DocumentType[]>([]);
  readonly _selectedCategory = signal<DocumentType | null>(null);
  readonly _selectedSubtype = signal<DocumentSubtype | null>(null);
  readonly _customTypeName = signal('');
  readonly _subStep = signal<SubStep>('category');

  readonly _filteredSubtypes = computed(() => {
    const cat = this._selectedCategory();
    if (!cat) return [];
    return cat.subtypes;
  });

  readonly _isCustomSubtype = computed(
    () => this._selectedSubtype()?.code.startsWith('CUSTOM_') ?? false
  );

  readonly _confirmDisabled = computed(() => {
    const sub = this._selectedSubtype();
    if (!sub) return true;
    if (sub.code.startsWith('CUSTOM_') && !this._customTypeName().trim()) return true;
    return false;
  });

  /** Step configs for `<app-step-indicator>`. Matches the shape every
   *  other wizard modal in the app (`org-selector-modal`, `create-
   *  control-modal`, `create-task-modal`, `create-vendor-modal`,
   *  `processing-activity-modal`, `vendor-request-modal`) uses — one
   *  visual grammar for progress across all creation/edit wizards. */
  protected readonly stepConfigs = computed<readonly StepConfig[]>(() => {
    this.translationVersion();
    const category = this._selectedCategory();
    const subtype = this._selectedSubtype();
    return [
      {
        key: 'category',
        label: this.transloco.translate('content_type_modal.step_category'),
        detail: category ? localizedName(category, this.locale()) : undefined,
      },
      {
        key: 'subtype',
        label: this.transloco.translate('content_type_modal.step_type'),
        detail: this._isCustomSubtype()
          ? this._customTypeName().trim()
          : subtype
            ? localizedName(subtype, this.locale())
            : undefined,
      },
    ];
  });

  /** High-water mark for the step indicator. Advances to `subtype`
   *  once the user has picked a category (making that step reachable);
   *  never regresses — the indicator's contract expects a monotonic
   *  signal so revisiting an earlier step doesn't wipe the "visited"
   *  ring on the further one. */
  private readonly _maxReachedStep = signal<SubStep>('category');
  protected readonly maxReachedStep = this._maxReachedStep.asReadonly();

  protected readonly modalConfig = computed<ModalConfig>(() => {
    this.translationVersion();
    const inSubtype = this._subStep() === 'subtype';
    return {
      title: this.transloco.translate('content_type_modal.modal_title'),
      size: 'medium',
      showLogo: true,
      showCloseButton: true,
      showFooter: inSubtype,
      primaryButtonText: this.transloco.translate('content_type_modal.modal_primary'),
      secondaryButtonText: this.transloco.translate('common.back'),
      primaryButtonDisabled: this._confirmDisabled(),
    };
  });

  onCategorySelect(cat: DocumentType): void {
    this._selectedCategory.set(cat);
    this._selectedSubtype.set(null);
    this._customTypeName.set('');
    this._subStep.set('subtype');
    this._maxReachedStep.set('subtype');
  }

  onSubtypeSelect(sub: DocumentSubtype): void {
    this._selectedSubtype.set(sub);
    if (!sub.code.startsWith('CUSTOM_')) {
      this._customTypeName.set('');
    }
  }

  /** Step-indicator jump — only the `category` step is a valid back
   *  destination in this two-step wizard, and the wizard clears the
   *  in-progress subtype on jump so re-entering it forces an explicit
   *  category confirm. */
  protected onStepClicked(key: string): void {
    if (key === 'category') {
      this._subStep.set('category');
      this._selectedSubtype.set(null);
      this._customTypeName.set('');
    } else if (key === 'subtype' && this._selectedCategory()) {
      this._subStep.set('subtype');
    }
  }

  onConfirm(): void {
    const cat = this._selectedCategory();
    const sub = this._selectedSubtype();
    if (!cat || !sub) return;
    this.selected.emit({
      typeId: cat.id,
      subtypeId: sub.id,
      subtypeCode: sub.code,
      subtypeName: sub.name,
      subtypeNameDa: sub.name_da,
      customTypeName: sub.code.startsWith('CUSTOM_') ? this._customTypeName().trim() : null,
    });
    this._reset();
  }

  onBack(): void {
    this._subStep.set('category');
    this._selectedSubtype.set(null);
    this._customTypeName.set('');
  }

  onCancel(): void {
    this._reset();
    this.cancelled.emit();
  }

  private _reset(): void {
    this._selectedCategory.set(null);
    this._selectedSubtype.set(null);
    this._customTypeName.set('');
    this._subStep.set('category');
    this._maxReachedStep.set('category');
  }
}
